import Vue from 'vue'

// window.{{globals.loadedCallback}} hook
// Useful for jsdom testing or plugins (https://github.com/tmpvar/jsdom#dealing-with-asynchronous-script-loading)
if (process.client) {
  window.onNuxtReadyCbs = []
  window.onNuxtReady = (cb) => {
    window.onNuxtReadyCbs.push(cb)
  }
}

export function empty () {}

export function globalHandleError (error) {
  if (Vue.config.errorHandler) {
    Vue.config.errorHandler(error)
  }
}

export function interopDefault (promise) {
  return promise.then(m => m.default || m)
}

export function hasFetch(vm) {
  return vm.$options && typeof vm.$options.fetch === 'function' && !vm.$options.fetch.length
}
export function getChildrenComponentInstancesUsingFetch(vm, instances = []) {
  const children = vm.$children || []
  for (const child of children) {
    if (child.$fetch) {
      instances.push(child)
      continue; // Don't get the children since it will reload the template
    }
    if (child.$children) {
      getChildrenComponentInstancesUsingFetch(child, instances)
    }
  }
  return instances
}

export function applyAsyncData (Component, asyncData) {
  if (
    // For SSR, we once all this function without second param to just apply asyncData
    // Prevent doing this for each SSR request
    !asyncData && Component.options.__hasNuxtData
  ) {
    return
  }

  const ComponentData = Component.options._originDataFn || Component.options.data || function () { return {} }
  Component.options._originDataFn = ComponentData

  Component.options.data = function () {
    const data = ComponentData.call(this, this)
    if (this.$ssrContext) {
      asyncData = this.$ssrContext.asyncData[Component.cid]
    }
    return { ...data, ...asyncData }
  }

  Component.options.__hasNuxtData = true

  if (Component._Ctor && Component._Ctor.options) {
    Component._Ctor.options.data = Component.options.data
  }
}

export function sanitizeComponent (Component) {
  // If Component already sanitized
  if (Component.options && Component._Ctor === Component) {
    return Component
  }
  if (!Component.options) {
    Component = Vue.extend(Component) // fix issue #6
    Component._Ctor = Component
  } else {
    Component._Ctor = Component
    Component.extendOptions = Component.options
  }
  // If no component name defined, set file path as name, (also fixes #5703)
  if (!Component.options.name && Component.options.__file) {
    Component.options.name = Component.options.__file
  }
  return Component
}

export function getMatchedComponents (route, matches = false, prop = 'components') {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m[prop]).map((key) => {
      matches && matches.push(index)
      return m[prop][key]
    })
  }))
}

export function getMatchedComponentsInstances (route, matches = false) {
  return getMatchedComponents(route, matches, 'instances')
}

export function flatMapComponents (route, fn) {
  return Array.prototype.concat.apply([], route.matched.map((m, index) => {
    return Object.keys(m.components).reduce((promises, key) => {
      if (m.components[key]) {
        promises.push(fn(m.components[key], m.instances[key], m, key, index))
      } else {
        delete m.components[key]
      }
      return promises
    }, [])
  }))
}

export function resolveRouteComponents (route, fn) {
  return Promise.all(
    flatMapComponents(route, async (Component, instance, match, key) => {
      // If component is a function, resolve it
      if (typeof Component === 'function' && !Component.options) {
        Component = await Component()
      }
      match.components[key] = Component = sanitizeComponent(Component)
      return typeof fn === 'function' ? fn(Component, instance, match, key) : Component
    })
  )
}

export async function getRouteData (route) {
  if (!route) {
    return
  }
  // Make sure the components are resolved (code-splitting)
  await resolveRouteComponents(route)
  // Send back a copy of route with meta based on Component definition
  return {
    ...route,
    meta: getMatchedComponents(route).map((Component, index) => {
      return { ...Component.options.meta, ...(route.matched[index] || {}).meta }
    })
  }
}

export async function setContext (app, context) {
  // If context not defined, create it
  if (!app.context) {
    app.context = {
      isStatic: process.static,
      isDev: false,
      isHMR: false,
      app,
      store: app.store,
      payload: context.payload,
      error: context.error,
      base: '/',
      env: {"dragonStrikeContractAddress":"0x45Ae66b6B147491362861B0A5ED8479F44c6374f","googleOptimizeId":"OPT-MMBVRNV","flaggedGamesToInclude":"Town Crush","townCrushLink":"https://super-secret-project.gala.games/","showTownCrushPage":true,"gcActionsDisabled":true,"batchActions":true,"userAttributionPlugin":{"adaptors":{"debugAdaptor":{"enabled":false},"activeCampaignAdaptor":{"enabled":true,"accountId":"91470544"},"galaAnalyticsAdaptor":{"enabled":true,"publisherId":"4Uu01MwCIzeiMO7TxzS0wCgAA4PJpQDCaD0jdA6w6HevmRZxhawhZCaOGkSMyWka","publisherGroup":"product","reportingEndpoint":"https://alpha-data.gala.com","fpGamesEndpoint":"https://fp.gala.games","fpComEndpoint":"https://fp.gala.com","environment":"prod"},"addShoppersAdaptor":{"enabled":true,"accountId":"634f0c164e927a04d268dcd5"}}},"fortitudeIsReleased":true,"townCrushIsReleased":false,"townCrushIsReleasedInternally":true,"showBattlestarGalactica":true,"showEternalParadox":true,"showGrit":true,"sanityProjectId":"j6di4pid","sanityDataset":"production","sanityApiVersion":"2022-05-24","sanityToken":"","showEpicGamesLinkedAccount":true,"showSteamLinkedAccount":true,"epicStoreAccountLinkUrl":"https://www.epicgames.com/id/login?client_id=xyza7891zCCq6fuvRE1HeDSBqai5YZcC&response_type=code&scope=basic_profile","galaBaseDomain":"gala.games","enabledHonor":true,"galaItemContractAddress":"0xc36cF0cFcb5d905B8B513860dB0CFE63F6Cf9F5c","spiderTanksItemContractAddress":"0x2A6d6a082C410a195157EC4caf67CB9fD718f087","championsArenaItemContractAddress":"0x8ac865B70a212Af1825bdff9f0561a26A2a94b37","pokerGoItemContractAddress":"0x4aA07Ee600634465984002Ad145aBAE6bc9338F5","legacyItemContractAddress":"0x8F66A1247A7e2A48998d02593407474B75a7ceD6","echoesOfEmpireItemContractAddress":"0x6643183e7117f9d69f06616be1a3913b35430CC2","eternalParadoxItemContractAddress":"0x39778Ab72312B2876F0BA08634Bdc7B3bA769f3a","meowPetsItemContractAddress":"0x9ADC4604A6D1a244A2CBff6C624CE64dc0d3ed40","townStarItemContractAddress":"0x9F2b334139a3c130E2E0B96d5C8FdbEa3DA06a88","voxContractAddress":"0xad9Fd7cB4fC7A0fBCE08d64068f60CbDE22Ed34C","twdVoxContractAddress":"0x338BE3d8D0209815601e72F7A04Ac7F37d61564b","mirandusVoxContractAddress":"0xf76179bb0924BA7da8E7b7Fc2779495D7A7939d8","curryContractAddress":"0x9cA3A9a3aA59C7ddd61C29f6b0540ad9988AeDE6","blueRealmContractAddress":"0x2378e88624c7DfDF73A6B02dEd8733449916FDBa","gritItemContractAddress":"0x00c41dAe62109F5Eb1B58Cdf82e08FEfd16A67fe","ethNodeUrl":"https://eth8.gala.games","enableRestrictionsService":true,"nodeDashboardUrl":"https://node.gala.com","ethereumBridgeContractAddress":"0x9f452b7cC24e6e6FA690fe77CF5dD2ba3DbF1ED9","galaGyriProductId":"638e75c417ab57e7e016e939","townSwapProductId":"63f78410e7b08950028662a1","orderPaymentContractAddress":"0x50343Db63b24981397C6411Ae2e4A11Bf5f69D4B","w3wConnectionEnabled":true,"referralDomains":".gala.games,.gala.com","referralCookiePrefix":"","galaComUrl":"https://gala.com","enabledFeatureUserActivityLog":true,"creatorsPublicUrl":"https://creators-public-platform-creators-prod-gala.gala.com/api","creatorsEnv":"production","licenseListEnabled":false,"nodeMintingEnabled":false,"galaLauncherPort":32123,"brand":"gala","creditCardIsEnabled":false,"currencySwapping":true,"discordConnectLink":"https://discord.gala.games/authorize?redirect_uri=https://app.gala.games","displayGameInIframe":true,"echosOfEmpireLatestVersion":"1.0.0","echosOfEmpireIsReleased":false,"echosOfEmpireIsReleasedWin":false,"echosOfEmpireIsReleasedMac":false,"fortitudeIsReleasedWin":false,"fortitudeIsReleasedMac":false,"fortitudeLatestVersion":"0.5.2","galaLauncherIntallerUrlMac":"https://static.gala.games/installers/GalaLauncher-installer-mac-1.1.6.pkg","galaLauncherIntallerUrlWin":"https://static.gala.games/installers/GalaLauncherSetup-1.1.6.exe","galaLauncherLatestVersion":"1.1.6","galaverseTicketsIsReleased":true,"gatewayUri":"https://walletsrv.gala.games/gateway","gtmContainerId":"GTM-WQGGMCN","isProd":true,"mirandusLatestVersion":"0.0.84.0","mirandusIsReleased":true,"mirandusIsReleasedWin":true,"mirandusIsReleasedMac":false,"networkType":"prod","nodeEnv":"production","recaptchaSitekey":"6LeBRZAaAAAAACeEAmU4sHlEQU1t8CKahCpLhc4-","server":"https://walletsrv.gala.games/graphql","showConvertCoinScreens":false,"showBuyCoinScreens":"true","spiderTanksLatestVersion":"0.6.4.126","spiderTanksIsReleased":true,"spiderTanksIsReleasedWin":true,"spiderTanksIsReleasedMac":false,"spiderTanksEarlyAccessStart":"1631815200000","spiderTanksEarlyAccessEnd":"1631901600000","townStarLink":"https://townstar.sandbox-games.com/launch","townStarEmphasizedStoreCategory":"skins","townStarIsReleased":true,"legacyIsReleased":false,"legacyIsReleasedWin":false,"legacyIsReleasedMac":false,"lastExpeditionIsReleased":false,"legendsRebornIsReleased":false,"legendsRebornIsReleasedWin":true,"legendsRebornIsReleasedMac":true,"legendsRebornLatestVersion":"0.3.0.739480","displayTSNodeTimestamp":"1637708400000","walkingDeadIsReleased":false,"walkingDeadIsReleasedWin":false,"walkingDeadIsReleasedMac":false,"superiorIsReleased":true,"superiorIsReleasedWin":false,"superiorIsReleasedMac":false,"superiorLatestVersion":"0.0.5","auth0Audience":"https://app.gala.games/platformserver","auth0Domain":"auth.gala.games","auth0MfaDomain":"galagames-prod.us.auth0.com","auth0ClientId":"uwVgH2Ia1kRiEjZRVUoYTTfgMy8ZhlrG","twdVoxBaseId":"0x02fe00000000000000000000000000000000","disabledSocialConnections":"twitter"}
    }
    // Only set once
    if (!process.static && context.req) {
      app.context.req = context.req
    }
    if (!process.static && context.res) {
      app.context.res = context.res
    }
    if (context.ssrContext) {
      app.context.ssrContext = context.ssrContext
    }
    app.context.redirect = (status, path, query) => {
      if (!status) {
        return
      }
      app.context._redirected = true
      // if only 1 or 2 arguments: redirect('/') or redirect('/', { foo: 'bar' })
      let pathType = typeof path
      if (typeof status !== 'number' && (pathType === 'undefined' || pathType === 'object')) {
        query = path || {}
        path = status
        pathType = typeof path
        status = 302
      }
      if (pathType === 'object') {
        path = app.router.resolve(path).route.fullPath
      }
      // "/absolute/route", "./relative/route" or "../relative/route"
      if (/(^[.]{1,2}\/)|(^\/(?!\/))/.test(path)) {
        app.context.next({
          path,
          query,
          status
        })
      } else {
        path = formatUrl(path, query)
        if (process.server) {
          app.context.next({
            path,
            status
          })
        }
        if (process.client) {
          // https://developer.mozilla.org/en-US/docs/Web/API/Location/replace
          window.location.replace(path)

          // Throw a redirect error
          throw new Error('ERR_REDIRECT')
        }
      }
    }
    if (process.server) {
      app.context.beforeNuxtRender = fn => context.beforeRenderFns.push(fn)
    }
    if (process.client) {
      app.context.nuxtState = window.__NUXT__
    }
  }

  // Dynamic keys
  const [currentRouteData, fromRouteData] = await Promise.all([
    getRouteData(context.route),
    getRouteData(context.from)
  ])

  if (context.route) {
    app.context.route = currentRouteData
  }

  if (context.from) {
    app.context.from = fromRouteData
  }

  app.context.next = context.next
  app.context._redirected = false
  app.context._errored = false
  app.context.isHMR = false
  app.context.params = app.context.route.params || {}
  app.context.query = app.context.route.query || {}
}

export function middlewareSeries (promises, appContext) {
  if (!promises.length || appContext._redirected || appContext._errored) {
    return Promise.resolve()
  }
  return promisify(promises[0], appContext)
    .then(() => {
      return middlewareSeries(promises.slice(1), appContext)
    })
}

export function promisify (fn, context) {
  let promise
  if (fn.length === 2) {
    // fn(context, callback)
    promise = new Promise((resolve) => {
      fn(context, function (err, data) {
        if (err) {
          context.error(err)
        }
        data = data || {}
        resolve(data)
      })
    })
  } else {
    promise = fn(context)
  }

  if (promise && promise instanceof Promise && typeof promise.then === 'function') {
    return promise
  }
  return Promise.resolve(promise)
}

// Imported from vue-router
export function getLocation (base, mode) {
  let path = decodeURI(window.location.pathname)
  if (mode === 'hash') {
    return window.location.hash.replace(/^#\//, '')
  }
  // To get matched with sanitized router.base add trailing slash
  if (base && (path.endsWith('/') ? path : path + '/').startsWith(base)) {
    path = path.slice(base.length)
  }
  return (path || '/') + window.location.search + window.location.hash
}

// Imported from path-to-regexp

/**
 * Compile a string to a template function for the path.
 *
 * @param  {string}             str
 * @param  {Object=}            options
 * @return {!function(Object=, Object=)}
 */
export function compile (str, options) {
  return tokensToFunction(parse(str, options), options)
}

export function getQueryDiff (toQuery, fromQuery) {
  const diff = {}
  const queries = { ...toQuery, ...fromQuery }
  for (const k in queries) {
    if (String(toQuery[k]) !== String(fromQuery[k])) {
      diff[k] = true
    }
  }
  return diff
}

export function normalizeError (err) {
  let message
  if (!(err.message || typeof err === 'string')) {
    try {
      message = JSON.stringify(err, null, 2)
    } catch (e) {
      message = `[${err.constructor.name}]`
    }
  } else {
    message = err.message || err
  }
  return {
    ...err,
    message,
    statusCode: (err.statusCode || err.status || (err.response && err.response.status) || 500)
  }
}

/**
 * The main path matching regexp utility.
 *
 * @type {RegExp}
 */
const PATH_REGEXP = new RegExp([
  // Match escaped characters that would otherwise appear in future matches.
  // This allows the user to escape special characters that won't transform.
  '(\\\\.)',
  // Match Express-style parameters and un-named parameters with a prefix
  // and optional suffixes. Matches appear as:
  //
  // "/:test(\\d+)?" => ["/", "test", "\d+", undefined, "?", undefined]
  // "/route(\\d+)"  => [undefined, undefined, undefined, "\d+", undefined, undefined]
  // "/*"            => ["/", undefined, undefined, undefined, undefined, "*"]
  '([\\/.])?(?:(?:\\:(\\w+)(?:\\(((?:\\\\.|[^\\\\()])+)\\))?|\\(((?:\\\\.|[^\\\\()])+)\\))([+*?])?|(\\*))'
].join('|'), 'g')

/**
 * Parse a string for the raw tokens.
 *
 * @param  {string}  str
 * @param  {Object=} options
 * @return {!Array}
 */
function parse (str, options) {
  const tokens = []
  let key = 0
  let index = 0
  let path = ''
  const defaultDelimiter = (options && options.delimiter) || '/'
  let res

  while ((res = PATH_REGEXP.exec(str)) != null) {
    const m = res[0]
    const escaped = res[1]
    const offset = res.index
    path += str.slice(index, offset)
    index = offset + m.length

    // Ignore already escaped sequences.
    if (escaped) {
      path += escaped[1]
      continue
    }

    const next = str[index]
    const prefix = res[2]
    const name = res[3]
    const capture = res[4]
    const group = res[5]
    const modifier = res[6]
    const asterisk = res[7]

    // Push the current path onto the tokens.
    if (path) {
      tokens.push(path)
      path = ''
    }

    const partial = prefix != null && next != null && next !== prefix
    const repeat = modifier === '+' || modifier === '*'
    const optional = modifier === '?' || modifier === '*'
    const delimiter = res[2] || defaultDelimiter
    const pattern = capture || group

    tokens.push({
      name: name || key++,
      prefix: prefix || '',
      delimiter,
      optional,
      repeat,
      partial,
      asterisk: Boolean(asterisk),
      pattern: pattern ? escapeGroup(pattern) : (asterisk ? '.*' : '[^' + escapeString(delimiter) + ']+?')
    })
  }

  // Match any characters still remaining.
  if (index < str.length) {
    path += str.substr(index)
  }

  // If the path exists, push it onto the end.
  if (path) {
    tokens.push(path)
  }

  return tokens
}

/**
 * Prettier encoding of URI path segments.
 *
 * @param  {string}
 * @return {string}
 */
function encodeURIComponentPretty (str, slashAllowed) {
  const re = slashAllowed ? /[?#]/g : /[/?#]/g
  return encodeURI(str).replace(re, (c) => {
    return '%' + c.charCodeAt(0).toString(16).toUpperCase()
  })
}

/**
 * Encode the asterisk parameter. Similar to `pretty`, but allows slashes.
 *
 * @param  {string}
 * @return {string}
 */
function encodeAsterisk (str) {
  return encodeURIComponentPretty(str, true)
}

/**
 * Escape a regular expression string.
 *
 * @param  {string} str
 * @return {string}
 */
function escapeString (str) {
  return str.replace(/([.+*?=^!:${}()[\]|/\\])/g, '\\$1')
}

/**
 * Escape the capturing group by escaping special characters and meaning.
 *
 * @param  {string} group
 * @return {string}
 */
function escapeGroup (group) {
  return group.replace(/([=!:$/()])/g, '\\$1')
}

/**
 * Expose a method for transforming tokens into the path function.
 */
function tokensToFunction (tokens, options) {
  // Compile all the tokens into regexps.
  const matches = new Array(tokens.length)

  // Compile all the patterns before compilation.
  for (let i = 0; i < tokens.length; i++) {
    if (typeof tokens[i] === 'object') {
      matches[i] = new RegExp('^(?:' + tokens[i].pattern + ')$', flags(options))
    }
  }

  return function (obj, opts) {
    let path = ''
    const data = obj || {}
    const options = opts || {}
    const encode = options.pretty ? encodeURIComponentPretty : encodeURIComponent

    for (let i = 0; i < tokens.length; i++) {
      const token = tokens[i]

      if (typeof token === 'string') {
        path += token

        continue
      }

      const value = data[token.name || 'pathMatch']
      let segment

      if (value == null) {
        if (token.optional) {
          // Prepend partial segment prefixes.
          if (token.partial) {
            path += token.prefix
          }

          continue
        } else {
          throw new TypeError('Expected "' + token.name + '" to be defined')
        }
      }

      if (Array.isArray(value)) {
        if (!token.repeat) {
          throw new TypeError('Expected "' + token.name + '" to not repeat, but received `' + JSON.stringify(value) + '`')
        }

        if (value.length === 0) {
          if (token.optional) {
            continue
          } else {
            throw new TypeError('Expected "' + token.name + '" to not be empty')
          }
        }

        for (let j = 0; j < value.length; j++) {
          segment = encode(value[j])

          if (!matches[i].test(segment)) {
            throw new TypeError('Expected all "' + token.name + '" to match "' + token.pattern + '", but received `' + JSON.stringify(segment) + '`')
          }

          path += (j === 0 ? token.prefix : token.delimiter) + segment
        }

        continue
      }

      segment = token.asterisk ? encodeAsterisk(value) : encode(value)

      if (!matches[i].test(segment)) {
        throw new TypeError('Expected "' + token.name + '" to match "' + token.pattern + '", but received "' + segment + '"')
      }

      path += token.prefix + segment
    }

    return path
  }
}

/**
 * Get the flags for a regexp from the options.
 *
 * @param  {Object} options
 * @return {string}
 */
function flags (options) {
  return options && options.sensitive ? '' : 'i'
}

/**
 * Format given url, append query to url query string
 *
 * @param  {string} url
 * @param  {string} query
 * @return {string}
 */
function formatUrl (url, query) {
  let protocol
  const index = url.indexOf('://')
  if (index !== -1) {
    protocol = url.substring(0, index)
    url = url.substring(index + 3)
  } else if (url.startsWith('//')) {
    url = url.substring(2)
  }

  let parts = url.split('/')
  let result = (protocol ? protocol + '://' : '//') + parts.shift()

  let path = parts.join('/')
  if (path === '' && parts.length === 1) {
    result += '/'
  }

  let hash
  parts = path.split('#')
  if (parts.length === 2) {
    [path, hash] = parts
  }

  result += path ? '/' + path : ''

  if (query && JSON.stringify(query) !== '{}') {
    result += (url.split('?').length === 2 ? '&' : '?') + formatQuery(query)
  }
  result += hash ? '#' + hash : ''

  return result
}

/**
 * Transform data object to query string
 *
 * @param  {object} query
 * @return {string}
 */
function formatQuery (query) {
  return Object.keys(query).sort().map((key) => {
    const val = query[key]
    if (val == null) {
      return ''
    }
    if (Array.isArray(val)) {
      return val.slice().map(val2 => [key, '=', val2].join('')).join('&')
    }
    return key + '=' + val
  }).filter(Boolean).join('&')
}

export function addLifecycleHook(vm, hook, fn) {
  if (!vm.$options[hook]) {
    vm.$options[hook] = []
  }
  if (!vm.$options[hook].includes(fn)) {
    vm.$options[hook].push(fn)
  }
}

export function urlJoin () {
  return [].slice
    .call(arguments)
    .join('/')
    .replace(/\/+/g, '/')
    .replace(':/', '://')
}

export function stripTrailingSlash (path) {
  return path.replace(/\/+$/, '') || '/'
}

export function isSamePath (p1, p2) {
  return stripTrailingSlash(p1) === stripTrailingSlash(p2)
}

export function setScrollRestoration (newVal) {
  try {
    window.history.scrollRestoration = newVal;
  } catch(e) {}
}
